import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { useSidePanel } from "../../shared/hooks/use-side-panel";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleChevronLeft, faCircleChevronRight, faFile } from "@fortawesome/free-solid-svg-icons";
import Accordion from "../accordion";
import { ToggleSwitch } from "../form-inputs/inputs";
import { DigitalLiteSwitchModal } from "../digital-lite-switch/digital-lite-switch-modal";
import { TaskContext } from "contexts/task";
import { useAggregatedProductInfo } from "shared/hooks/api/use-aggregated-product-info";
import { useDigitalLiteModal } from "../../shared/hooks/use-digital-lite-modal";
import { useViewOnlyPermissions } from "shared/hooks/use-view-only-permissions";
import { useHasPermission } from "shared/hooks/use-has-permission";
import { PermissionType } from "shared/constants/permission-type";
import { PermissionSource } from "shared/constants/permission-source";
import { TaskName } from "shared/constants/task-name";
import { RedoSection } from "./components/redo-section";
import { SectionHeader } from "./components/section-header";
import { useRedo } from "shared/hooks/use-redo";
import { useSendToGdsn } from "shared/hooks/api/use-send-to-gdsn";
import * as notify from "shared/notify";
import { useMatch } from "react-router-dom";

export const SidePanel = ({ disableControls = false }) => {
    const { isSidePanelOpen, onToggle, onEnableHeroFeature, onDisableCodingReject } = useSidePanel();
    const { task } = useContext(TaskContext);
    const isPhotographyView = useMatch("/photography/:id/:wip") !== null;
    const [isDigitalLiteSwitchToggled, setDigitalLiteSwitch] = useState(false);
    const [sendToGdsn, setSendToGdsn] = useState(false);
    const modalProps = useDigitalLiteModal(
        task.context.codingWipKey,
        false,
        () => {
            setDigitalLiteSwitch(false);
        },
        PermissionSource.LEFT_RAIL
    );
    const { mutate: updateSendToGdsn, isLoading: isSavingAttribute } = useSendToGdsn();
    const { isLoading, data: details } = useAggregatedProductInfo(task.context.shipmentProductKey);
    const [isProcessing, redoTask] = useRedo(TaskName.MEASURING, PermissionSource.LEFT_RAIL);
    const [isProcessingPhotography, redoPhotographyTask] = useRedo(TaskName.PHOTOGRAPHY, PermissionSource.LEFT_RAIL);
    const shouldSendToGdsn = details && details.sendToGdsn;
    const isVODigitalLiteStatus = useViewOnlyPermissions(PermissionType.EDIT_DIGITAL_LITE_STATUS);
    const isVORedoMeasurements = useViewOnlyPermissions(PermissionType.REDO_TASK, PermissionSource.LEFT_RAIL, TaskName.MEASURING);
    const isVORedoPhotography = useViewOnlyPermissions(PermissionType.REDO_TASK, PermissionSource.LEFT_RAIL, TaskName.PHOTOGRAPHY);
    const isVOSendToGdsn = useViewOnlyPermissions(PermissionType.EDIT_ATTRIBUTE, PermissionSource.LEFT_RAIL);
    const canRedoPhotography = useHasPermission(PermissionType.REDO_TASK, PermissionSource.LEFT_RAIL, TaskName.PHOTOGRAPHY);
    const isBrandVerified = task.context.workflowTypeId === 2;

    useEffect(() => {
        if (details) {
            onEnableHeroFeature(details.hasMobileBannerHeroFeature);
            onDisableCodingReject(details.hasSalesSampleFeature);
        }
    }, [details]);

    useEffect(() => {
        if (shouldSendToGdsn !== undefined) {
            setSendToGdsn(shouldSendToGdsn);
        }
    }, [shouldSendToGdsn]);

    const handleDigitalLiteSwitch = (name, value) => {
        if (value) {
            setDigitalLiteSwitch(true);
            modalProps.openModal(true);
        }
    };

    const handleSendToGDSN = (name, value) => {
        setSendToGdsn(value);
        updateSendToGdsn(
            { sendToGdsn: value, wipKey: task.context.codingWipKey, taskKey: task.key },
            {
                onSuccess: () => {
                    notify.success("Attribute saved");
                },
            }
        );
    };

    if (isLoading || !details) {
        return null;
    }

    const { measurements, shipmentResources, shipmentTypes, isMeasurementTaskCompleted, areAllPhotographyTasksCompleted } = details;
    const isDigitalLite = shipmentTypes && shipmentTypes.some(({ isChosen, value }) => isChosen && value === 2);
    const isPhysical = shipmentTypes && shipmentTypes.some(({ isChosen, value }) => isChosen && value === 0);
    const enablePhotographyRedo = areAllPhotographyTasksCompleted && canRedoPhotography;

    if (measurements.length === 0 && shipmentResources.length === 0 && isPhysical) {
        return null;
    }

    return (
        <>
            <DigitalLiteSwitchModal
                {...modalProps}
                upc={task.upc}
            />
            <SidePanelContainer open={isSidePanelOpen}>
                <ToggleIconContainer open={isSidePanelOpen}>
                    {isSidePanelOpen ? (
                        <FontAwesomeIcon
                            icon={faCircleChevronLeft}
                            onClick={() => onToggle(false)}
                        />
                    ) : (
                        <FontAwesomeIcon
                            icon={faCircleChevronRight}
                            onClick={() => onToggle(true)}
                        />
                    )}
                </ToggleIconContainer>
                <SidePanelBody visible={isSidePanelOpen}>
                    <Accordion
                        loadOnOpen={false}
                        enableSeparator={false}
                        stretchBody={true}
                    >
                        <AccordionBody
                            label="Shipments"
                            className="flex-column"
                        >
                            {shipmentResources.length > 0 && (
                                <div>
                                    <SectionHeader>Resources</SectionHeader>
                                    {shipmentResources.map(({ name, downloadUri }) => {
                                        return (
                                            <a
                                                key={name}
                                                className="button -text -text-small"
                                                href={downloadUri}
                                                title={name}
                                                download={name}
                                            >
                                                <FontAwesomeIcon
                                                    className="m-r-8"
                                                    icon={faFile}
                                                />
                                                {name}
                                            </a>
                                        );
                                    })}
                                    <div className="h-separator" />
                                </div>
                            )}
                            {!task.context.isFinalized && !task.isCompleted && (
                                <div>
                                    <SectionHeader>UPC Actions</SectionHeader>
                                    {!isPhysical && (
                                        <ToggleSwitch
                                            className="font-sm"
                                            variant="small"
                                            name="digitalLiteSwitch"
                                            disabled={isDigitalLite || isVODigitalLiteStatus || disableControls}
                                            hasDisabledStyles={isDigitalLite || isVODigitalLiteStatus}
                                            value={isDigitalLiteSwitchToggled}
                                            label="Switch to digital lite"
                                            updateOnChange={handleDigitalLiteSwitch}
                                        />
                                    )}
                                    <ToggleSwitch
                                        className="font-sm"
                                        variant="small"
                                        name="sendToGDSN"
                                        disabled={isSavingAttribute || isVOSendToGdsn || disableControls}
                                        hasDisabledStyles={isSavingAttribute || isVOSendToGdsn}
                                        value={sendToGdsn}
                                        label="Send to GDSN"
                                        updateOnChange={handleSendToGDSN}
                                    />
                                    <div className="h-separator" />
                                </div>
                            )}
                            {measurements.length > 0 && (
                                <RedoSection
                                    title="Standard measurements"
                                    showRedo={isMeasurementTaskCompleted && !task.isCompleted && !isDigitalLite && !isBrandVerified}
                                    onRedo={redoTask}
                                    isDisabled={isVORedoMeasurements || isProcessing || disableControls}
                                >
                                    <div className="flex">
                                        {measurements.map(({ measurementType, height, width, depth, weight }) => (
                                            <Measurements
                                                key={measurementType}
                                                length={measurements.length}
                                            >
                                                <MeasurementText>{measurementType}</MeasurementText>
                                                <div className="justify-content-between">
                                                    <div className="flex-column">
                                                        <MeasurementText>Height:</MeasurementText>
                                                        <MeasurementText>Width:</MeasurementText>
                                                        <MeasurementText>Depth:</MeasurementText>
                                                        <MeasurementText>Weight:</MeasurementText>
                                                    </div>
                                                    <div className="flex-column">
                                                        <MeasurementText>
                                                            {height.value} {height.uom}
                                                        </MeasurementText>
                                                        <MeasurementText>
                                                            {width.value} {width.uom}
                                                        </MeasurementText>
                                                        <MeasurementText>
                                                            {depth.value} {depth.uom}
                                                        </MeasurementText>
                                                        <MeasurementText>
                                                            {weight.value} {weight.uom}
                                                        </MeasurementText>
                                                    </div>
                                                </div>
                                            </Measurements>
                                        ))}
                                    </div>
                                </RedoSection>
                            )}
                        </AccordionBody>
                        <AccordionBody
                            hidden={isPhotographyView}
                            label="Photography"
                            className="flex-column"
                        >
                            <RedoSection
                                title="Reject to Photography"
                                onRedo={redoPhotographyTask}
                                isDisabled={
                                    isVORedoPhotography || isProcessingPhotography || disableControls || !enablePhotographyRedo || task.isCompleted
                                }
                                showRedo={canRedoPhotography}
                                showSeparator={false}
                            />
                        </AccordionBody>
                        <AccordionBody
                            hidden={true}
                            label="Data"
                        >
                            Data
                        </AccordionBody>
                        <AccordionBody
                            hidden={true}
                            label="Images"
                        >
                            Images
                        </AccordionBody>
                        <AccordionBody
                            hidden={true}
                            label="Other"
                        >
                            Other
                        </AccordionBody>
                    </Accordion>
                </SidePanelBody>
            </SidePanelContainer>
        </>
    );
};

const SidePanelContainer = styled.div`
    position: relative;
    height: auto;
    z-index: 1;
    background-color: #eff4f8;
    flex: 0 0 ${({ open }) => (open ? "250px" : 0)};
    border-right: ${({ open }) => (open ? "2px solid #00c5f3" : 0)};
`;

const SidePanelBody = styled.div`
    position: sticky;
    width: 100%;
    top: 55px;
    max-height: calc(100vh - 55px);
    overflow-y: auto;
    display: ${({ visible }) => (visible ? "block" : "none")};
`;

const ToggleIconContainer = styled.div`
    position: absolute;
    right: -25px;
    top: 10px;
`;

const AccordionBody = styled.div`
    display: flex;
    background-color: #dee7ef;
    padding: 8px 8px 8px 30px;
`;

const Measurements = styled.div`
    flex: ${({ length }) => (length === 1 ? 0.5 : 1)};

    &:first-of-type {
        margin-right: ${({ length }) => (length === 1 ? 0 : "10px")};
    }
`;

const MeasurementText = styled.span`
    font-size: 10px;
`;
